import { useEffect, useRef } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.tsx';
import MobileNav from './MobileNav.tsx';
import { useAuth } from '../store/auth.tsx';
import { useAppSelector } from '../store/hooks';
import { selectSessionKey } from '../store/authSlice';
import { getPageShellKey, getPageTransitionClass, useScrollRestoration } from '../hooks/useScrollRestoration.ts';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSync } from '../store/sync.tsx';

const Layout = () => {
  const { user, isLoading } = useAuth();
  const sessionKey = useAppSelector(selectSessionKey);
  const { t } = useTranslation();
  const location = useLocation();
  const scrollRef = useRef<HTMLElement>(null);
  useScrollRestoration(scrollRef);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const apply = () => {
      const gutter = el.offsetWidth - el.clientWidth;
      document.documentElement.style.setProperty('--content-scroll-gutter', `${gutter}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--content-scroll-gutter');
    };
  }, [user]);
  const pageTransitionClass = getPageTransitionClass(location.pathname);
  const pageShellKey = getPageShellKey(location.pathname);
  const { isOffline, pendingCount, isSyncing } = useSync();
  const showSyncBanner = isOffline || pendingCount > 0;

  if (isLoading && !user) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-4">
        <div className="spinner h-10 w-10" />
        <p className="font-semibold text-primary">{t('Загрузка...')}</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-shell">
      <Sidebar />
      <MobileNav />
      <main className="flex-1 flex flex-col min-w-0 min-h-0">
        <section
          ref={scrollRef}
          className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4 pt-[max(1.25rem,env(safe-area-inset-top,0px))] pb-32 md:py-8 md:px-10"
        >
          {showSyncBanner && (
            <div
              className={`sticky top-0 z-20 mb-4 -mt-[max(1.25rem,env(safe-area-inset-top,0px))] pt-[max(1.25rem,env(safe-area-inset-top,0px))] pb-2.5 flex items-center justify-center gap-1.5 text-xs font-semibold backdrop-blur-xl border-b ${
                isOffline
                  ? 'bg-amber-500/10 border-amber-500/20 text-amber-800'
                  : 'bg-sky-500/10 border-sky-500/20 text-sky-800'
              }`}
              role="status"
            >
              {isOffline ? (
                <>
                  <WifiOff size={14} className="shrink-0" />
                  <span>{t('Офлайн')}</span>
                  {pendingCount > 0 && (
                    <span className="opacity-80">· {t('{{count}} ожидают', { count: pendingCount })}</span>
                  )}
                </>
              ) : (
                <>
                  <RefreshCw size={14} className={`shrink-0 ${isSyncing ? 'animate-spin' : ''}`} />
                  <span>{t('{{count}} ожидают синхронизации', { count: pendingCount })}</span>
                </>
              )}
            </div>
          )}

          <div
            key={`${user.id}-${sessionKey}-${pageShellKey}`}
            className={`page-transition ${pageTransitionClass}`}
          >
            <Outlet />
          </div>
        </section>
      </main>
    </div>
  );
};

export default Layout;
